import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { FLOAT_TEXTURES_OK } from '../float-textures.js';
import { vdjColor, vdjBeatChroma, VdjPost, VdjComfort, VDJ_PRESETS, VDJ_DROP_FLASH } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Tout ce qui éclaire est un néon.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'hyperloop',
  family: 'geometric',
  danceability: 0.90,
  energyLevel: 0.88,
  bpmMin: 125,
  bpmMax: 180,
  movement: 0.95,
  tonality: 0.40,
  prefers: { gridStrength: [0.65, 1.0] },
  // every tube lives in a float texture read by the vertex shaders
  enabled: FLOAT_TEXTURES_OK
};

// HYPERLOOP : une course à grande vitesse dans un réseau de tubes néon.
// Drum'n'bass, techno, trance, hardstyle. Règles de kiosk-app/CLAUDE.md (R1 à
// R19), preset « party » de vdjLook.
//
// LE CHEMIN. Une courbe générée par SEGMENTS, un segment = une mesure de vol
// = 8 échantillons (un anneau tous les demi-temps, S = 1 unité). Chaque
// segment intègre un cap et une pente qui passent en douceur (smoothstep)
// d'une cible à la suivante : virage borné à 45° par mesure, pente à ±18°,
// donc courbure bornée PAR CONSTRUCTION (rayon >= ~7 unités pour un tube de
// rayon 1,4). Le repère de chaque échantillon est TRANSPORTÉ PARALLÈLEMENT
// (on retire au repère précédent sa composante sur la nouvelle tangente) :
// aucun retournement, quelle que soit la courbe. Tout est calculé côté CPU,
// une fois par segment, et écrit dans UNE texture flottante (256 colonnes) :
// lignes 0-2 le tube principal en tampon circulaire (centre, N, B), puis
// trois lignes par tube secondaire (embranchements, croisements, réseau du
// drop), ses paramètres en colonne 255. Les anneaux, les longerons et la
// paroi sont des instances qui lisent cette texture : rien d'autre n'est
// envoyé au GPU, sauf la couleur des anneaux à leur naissance (256 x 1).
//
// LE TEMPS. La distance parcourue est une fonction du compteur de temps :
// travel = T0 + 2 S x vitesse x (temps - B0), avec B0 calé sur un début de
// mesure et la vitesse (x1, x2, x4 en montée) qui ne change que sur un temps
// fort : un anneau est EXACTEMENT sur la caméra à chaque temps, et chaque
// segment commence sur un temps fort.
//
// LES BIFURCATIONS. Une mesure sur deux, le segment se dédouble : le tube
// suivi et une branche partent du même état ; sur la première demi-mesure
// ils s'écartent latéralement (ou verticalement) de ±1,6 rayon en douceur,
// puis leurs caps divergent. La caméra prend son tube au temps fort ; la
// branche (un tube secondaire, ses anneaux qui défilent) s'éloigne et se
// voit à travers la paroi jusqu'à disparaître dans le noir. Parfois un tube
// croise au-dessus ou en dessous, sans jonction. Tout est déterministe
// (graine par segment).
//
// LES ANNEAUX. Ruban en pixels (R1) : largeur constante à l'écran, la
// normale prise à la tangente PROJETÉE de l'anneau (un anneau vu de biais
// reste net). Chaque anneau naît au fond avec la couleur et l'épaisseur du
// moment (bandes, énergie) : le tube est l'historique de la musique qui
// arrive. L'anneau qui passe la caméra au prochain temps vire au blanc. Le
// rayon pulse de +8 % avec bands.low.
//
// LA CAMÉRA. Sur l'axe du tube (Catmull-Rom entre les échantillons), elle
// vise le chemin 6 unités plus loin ; roulis proportionnel à la courbure,
// borné à 20°, jamais de rotation complète (le haut reste le haut du monde).
// La pose ENTIÈRE est écrite à chaque image (piège de la dérive du
// scene-manager, voir Frames).
//
// LE RYTHME : TEMPS un anneau sur la caméra, séparation RGB vdj ; MESURE
// bifurcation (une sur deux) ou virage marqué ; PHRASE couleurs suivantes,
// longerons droits ou en hélice ; MONTÉE vitesse x2 puis x4 ; DROP la caméra
// jaillit du tube : 2 mesures dehors devant un réseau de dizaines de tubes
// qui s'entrelacent, flash, puis elle replonge dans le tube.

const TIER = {
  0: { rings: 50, longs: 4, crossings: false, normal: 8, net: 6, ringK: 32 },
  1: { rings: 80, longs: 6, crossings: true, normal: 12, net: 10, ringK: 40 },
  2: { rings: 80, longs: 8, crossings: true, normal: 12, net: 16, ringK: 40 }
};
const S = 1.0;                  // ring spacing = half a beat of travel
const BAR = 8;                  // samples per segment (one bar)
const R = 1.4;                  // tube radius
const CAP = 256;                // main tube ring buffer (samples)
const SIDE_LEN = 64;            // samples per side tube
const SIDE_RINGS = 32;          // one ring every two samples
const BACK = 24;                // rings kept behind the camera (seen from outside)
const LOW_PULSE = 0.08;
const MAX_TURN = THREE.MathUtils.degToRad(45);
const MIN_TURN = THREE.MathUtils.degToRad(25);
const FORK_TURN = THREE.MathUtils.degToRad(18);
const MAX_PITCH = THREE.MathUtils.degToRad(18);
// the Y: our tube moves aside only a little (comfort), the branch takes the
// rest; 3 R apart at the end of the half-bar, the walls no longer touch
const FORK_OFF_MAIN = 0.6 * R, FORK_OFF_BRANCH = 2.4 * R;
const MAX_ROLL = THREE.MathUtils.degToRad(20);
const BANK_K = 3.5;             // roll (rad) per unit of curvature: the sharpest bend allowed gives ~20 degrees
const LOOK = 6;                 // the camera aims this far along the path (x rate^0.6 when faster)
const AIM_TAU = 0.12;           // s, the aim's low-pass
const FOV_IN = 74, FOV_OUT = 52;
const FADE_IN = 30, FADE_OUT = 110;
const SAFE_FLOOR = 0.5;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const FALLBACK = ['#22d3ff', '#ff2bd6', '#ffd23f'];
const KIND = { branch: 1, crossing: 2, net: 3 };

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function sm(u) { u = clamp01(u); return u * u * (3 - 2 * u); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}
function rng(seed) { let s = (seed >>> 0) || 1; return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; }; }
const UP = new THREE.Vector3(0, 1, 0);
function dirOf(yaw, pitch, out) { return out.set(Math.sin(yaw) * Math.cos(pitch), Math.sin(pitch), -Math.cos(yaw) * Math.cos(pitch)); }

// ----------------------------------------------------------------- tubes on the CPU

// A tube grows sample by sample: position, tangent and a PARALLEL-TRANSPORTED
// normal (the previous normal minus its component on the new tangent) - the
// frame never flips. `step(yaw, pitch, offset)` integrates in 4 sub-steps.
class TubeState {
  constructor(p, yaw, pitch, n) {
    this.p = p.clone(); this.yaw = yaw; this.pitch = pitch;
    this.t = dirOf(yaw, pitch, new THREE.Vector3());
    this.n = n ? n.clone() : new THREE.Vector3().crossVectors(this.t, UP).cross(this.t).normalize().negate();
    if (this.n.lengthSq() < 0.5) this.n.set(1, 0, 0);
    this.base = this.p.clone();           // the path without the fork's lateral offset
  }
  clone() { const c = new TubeState(this.p, this.yaw, this.pitch, this.n); c.base.copy(this.base); c.t.copy(this.t); return c; }
}
const _d = new THREE.Vector3(), _q = new THREE.Vector3(), _q0 = new THREE.Vector3(), _q1 = new THREE.Vector3();
// one segment: n samples; yaw and pitch ease from the state's to the targets;
// offset(u) moves the tube sideways along q (the Y of a fork)
function growSegment(st, n, yawTo, pitchTo, out, offsetFn, qKind) {
  const yaw0 = st.yaw, pitch0 = st.pitch;
  for (let i = 1; i <= n; i++) {
    const SUB = 4;
    for (let k = 1; k <= SUB; k++) {
      const u = (i - 1 + k / SUB) / n;
      const yaw = yaw0 + (yawTo - yaw0) * sm(u), pitch = pitch0 + (pitchTo - pitch0) * sm(u);
      dirOf(yaw, pitch, _d);
      st.base.addScaledVector(_d, S / SUB);
      st.yaw = yaw; st.pitch = pitch;
    }
    const u = i / n;
    // the offset direction: horizontal right, or the tube's up
    dirOf(st.yaw, st.pitch, _d);
    _q.crossVectors(_d, UP).normalize();
    if (qKind === 'v') _q.crossVectors(_q, _d).normalize();
    const off = offsetFn ? offsetFn(u) : 0;
    const p = new THREE.Vector3().copy(st.base).addScaledVector(_q, off);
    const t = new THREE.Vector3().subVectors(p, st.p);
    if (t.lengthSq() < 1e-9) t.copy(_d); t.normalize();
    // parallel transport
    const nn = st.n.clone().addScaledVector(t, -st.n.dot(t)).normalize();
    st.p.copy(p); st.t.copy(t); st.n.copy(nn);
    out.push({ p: p.clone(), t: t.clone(), n: nn.clone(), b: new THREE.Vector3().crossVectors(t, nn).normalize() });
  }
  // the fork's offset stays: the base follows the tube from now on
  st.base.copy(st.p);
}

// ----------------------------------------------------------------- shaders

const SAFE_CHUNK = `
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  float safeAtt(vec2 ndc) {
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;

// the path: main tube rows 0-2 (ring buffer of CAP samples), side tube j rows
// 3+3j .. 5+3j (samples in columns 0..SIDE_LEN-1, parameters in column 255:
// row 0 = length, travel at birth, kind, flow sign; row 1 = colour)
const PATH_CHUNK = `
  uniform sampler2D uPath;
  uniform float uRows;
  uniform float uTravel;
  uniform float uMainBase;       // first main ring drawn
  uniform float uMainCount;      // rings ahead
  uniform float uRadius;
  uniform vec3 uCamPos;
  uniform float uFade;
  uniform float uNet;
  uniform float uBack;           // 1: draw the rings behind the camera (outside view)
  uniform vec2 uResolution;
  uniform float uTwist;          // longerons: radians per unit of path
  vec4 tx(float c, float r) { return texture2D(uPath, vec2((c + 0.5) / 256.0, (r + 0.5) / uRows)); }
  void mainSample(float k, out vec3 C, out vec3 N, out vec3 B) {
    float c = mod(k, ${CAP}.0);
    C = tx(c, 0.0).xyz; N = tx(c, 1.0).xyz; B = tx(c, 2.0).xyz;
  }
  void sideSample(float j, float u, out vec3 C, out vec3 N, out vec3 B) {
    float r = 3.0 + 3.0 * j;
    float i0 = floor(u), f = u - i0;
    C = mix(tx(i0, r).xyz, tx(i0 + 1.0, r).xyz, f);
    N = normalize(mix(tx(i0, r + 1.0).xyz, tx(i0 + 1.0, r + 1.0).xyz, f));
    B = normalize(mix(tx(i0, r + 2.0).xyz, tx(i0 + 1.0, r + 2.0).xyz, f));
  }
  // a side tube's ring position (samples) for ring r, flowing away from its start
  float sideU(float j, float r, vec4 prm) {
    float L = prm.x - 1.0;
    float flow = (uTravel - prm.y) / ${S.toFixed(1)};
    return mod(r * 2.0 + flow * prm.w, L);
  }
  float sideAlpha(float u, vec4 prm) {
    float L = prm.x - 1.0;
    float a = smoothstep(0.0, 4.0, u) * smoothstep(L, L - 6.0, u);
    if (prm.z > 2.5) a *= uNet;
    return a;
  }
  vec4 project(vec3 w) { return projectionMatrix * viewMatrix * vec4(w, 1.0); }
  // a point offset across a screen-space line by sidePx pixels, perpendicular to
  // the projected direction a -> b (both clip space)
  vec4 widen(vec4 ca, vec4 cb, float sidePx) {
    vec2 aspect = vec2(uResolution.x / uResolution.y, 1.0);
    vec2 na = ca.xy / ca.w * aspect, nb = cb.xy / cb.w * aspect;
    vec2 t = nb - na; float L = length(t); t = L > 1e-6 ? t / L : vec2(1.0, 0.0);
    vec2 n = vec2(-t.y, t.x);
    vec2 p = na + n * sidePx * 2.0 / uResolution.y;
    return vec4(p / aspect * ca.w, ca.z, ca.w);
  }
`;

// RINGS. Base: aK (0..K along the ring), aSide (-1..1 across). Instance: aTube
// (-1 main, j side), aRing.
function ringVertex(K) {
  return `
  precision highp float;
  attribute float aK;
  attribute float aSide;
  attribute float aTube;
  attribute float aRing;
  uniform sampler2D uRingCol;
  uniform float uFlashRing;
  uniform float uFlashAmt;
  uniform float uWhite;
  uniform float uBandsLow;
  ${PATH_CHUNK}
  varying vec3 vCol;
  varying float vA;
  varying float vAcross;     // pixels from the centre line
  varying float vHalf;       // half-width of the ribbon's lit part, pixels
  varying vec2 vNdc;
  void main() {
    vec3 C, N, B; vec3 col; float a = 1.0, halfPx = 3.0, white = 0.0;
    float rad = uRadius;
    if (aTube < 0.0) {
      float k = uMainBase + aRing;
      float ahead = k * ${S.toFixed(1)} - uTravel;
      if ((ahead < 0.35 && uBack < 0.5) || ahead > uMainCount * ${S.toFixed(1)}) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
      mainSample(k, C, N, B);
      vec4 rc = texture2D(uRingCol, vec2((mod(k, ${CAP}.0) + 0.5) / ${CAP}.0, 0.5));
      col = rc.rgb; halfPx = 0.5 * (3.0 + rc.a * 6.0);
      a = smoothstep(uMainCount, uMainCount - 8.0, ahead / ${S.toFixed(1)});
      // on each beat, the ring passing the camera (the one crossing the screen's
      // edge at that instant) flashes white
      if (abs(k - uFlashRing) < 0.5) white = uFlashAmt;
      rad *= 1.0 + ${LOW_PULSE.toFixed(2)} * uBandsLow;
    } else {
      vec4 prm = tx(255.0, 3.0 + 3.0 * aTube);
      if (prm.x < 2.0) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
      float u = sideU(aTube, aRing, prm);
      sideSample(aTube, u, C, N, B);
      col = tx(255.0, 4.0 + 3.0 * aTube).rgb;
      a = sideAlpha(u, prm);
      halfPx = 2.5;
    }
    float ang = 6.2831853 * aK / ${K}.0;
    float ang2 = ang + 0.05;
    vec3 P = C + rad * (cos(ang) * N + sin(ang) * B);
    vec3 P2 = C + rad * (cos(ang2) * N + sin(ang2) * B);
    vec4 c1 = project(P), c2 = project(P2);
    if (c1.w < 0.08 || c2.w < 0.08) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    float quadHalf = halfPx + 6.0;                // room for the halo (R12)
    vec4 cl = widen(c1, c2, aSide * quadHalf);
    gl_Position = cl;
    vAcross = aSide * quadHalf; vHalf = halfPx;
    float fade = exp(-length(P - uCamPos) / uFade);
    // a ring only a few pixels across is no longer a ring but a blob: far
    // rings fade by their PROJECTED radius (their width stays the same)
    vec4 cc = project(C);
    float radPx = cc.w > 0.08 ? length(c1.xy / c1.w - cc.xy / cc.w) * 0.5 * uResolution.y : 1e4;
    // (our tube: its far end piles up at the vanishing point; the other tubes,
    // seen from the side, keep their rings down to a few pixels)
    vA = a * fade * (aTube < 0.0 ? smoothstep(10.0, 42.0, radPx) : smoothstep(3.0, 12.0, radPx));
    vCol = mix(col, vec3(1.0), max(white, uWhite));
    vNdc = cl.xy / cl.w;
  }
`;
}
// a neon line by its distance in pixels (R1): white core, saturated sheath,
// faint halo that reaches zero before the quad's edge (R12)
const LINE_FRAGMENT = `
  precision highp float;
  uniform float uGain;
  varying vec3 vCol;
  varying float vA;
  varying float vAcross;
  varying float vHalf;
  varying vec2 vNdc;
  ${SAFE_CHUNK}
  void main() {
    if (vA <= 0.002) discard;
    float d = abs(vAcross);
    float coreHalf = min(0.75, vHalf * 0.3);
    float core = 1.0 - smoothstep(coreHalf - 0.5, coreHalf + 0.5, d);
    float sheath = 1.0 - smoothstep(vHalf - 0.5, vHalf + 0.5, d);
    float halo = exp(-max(d - vHalf, 0.0) / 2.5) * (1.0 - smoothstep(vHalf + 3.0, vHalf + 6.0, d)) * 0.3;
    vec3 c = vCol * (0.75 * sheath + halo * (1.0 - sheath));
    c = mix(c, mix(vec3(0.86, 0.84, 0.80), vCol, 0.15), core);   // just over the 0.78 bloom threshold
    gl_FragColor = vec4(c * vA * uGain * safeAtt(vNdc), 1.0);
  }
`;

// LONGERONS. Base: aEnd (0/1), aSide. Instance: aTube, aRing (interval), aLon.
const LONG_VERTEX = `
  precision highp float;
  attribute float aEnd;
  attribute float aSide;
  attribute float aTube;
  attribute float aRing;
  attribute float aLon;
  uniform float uLongs;
  uniform vec3 uLongCol;
  ${PATH_CHUNK}
  varying vec3 vCol;
  varying float vA;
  varying float vAcross;
  varying float vHalf;
  varying vec2 vNdc;
  void main() {
    vec3 C0, N0, B0, C1, N1, B1; float a = 1.0, s0, s1; vec3 col = uLongCol;
    float nL = uLongs;
    if (aTube < 0.0) {
      float k = uMainBase + aRing;
      float ahead = k * ${S.toFixed(1)} - uTravel;
      if (aLon >= nL || (ahead < -1.0 && uBack < 0.5) || ahead > uMainCount * ${S.toFixed(1)} - 1.0) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
      mainSample(k, C0, N0, B0); mainSample(k + 1.0, C1, N1, B1);
      s0 = k * ${S.toFixed(1)}; s1 = s0 + ${S.toFixed(1)};
      a = smoothstep(uMainCount, uMainCount - 8.0, ahead / ${S.toFixed(1)});
    } else {
      nL = min(uLongs, 4.0);
      vec4 prm = tx(255.0, 3.0 + 3.0 * aTube);
      if (prm.x < 2.0 || aLon >= nL) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
      float u = sideU(aTube, aRing, prm);
      float u1 = min(u + 2.0, prm.x - 1.0);
      sideSample(aTube, u, C0, N0, B0); sideSample(aTube, u1, C1, N1, B1);
      s0 = u; s1 = u1;
      col = tx(255.0, 4.0 + 3.0 * aTube).rgb;
      a = sideAlpha(u, prm) * 0.8;
    }
    float th0 = 6.2831853 * (aLon + 0.5) / nL + uTwist * s0;
    float th1 = 6.2831853 * (aLon + 0.5) / nL + uTwist * s1;
    vec3 P0 = C0 + uRadius * (cos(th0) * N0 + sin(th0) * B0);
    vec3 P1 = C1 + uRadius * (cos(th1) * N1 + sin(th1) * B1);
    vec4 c0 = project(P0), c1 = project(P1);
    // a segment crossing the camera plane is cut where it crosses
    if (c0.w < 0.08 && c1.w < 0.08) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    if (c0.w < 0.08) { float f = (0.08 - c0.w) / (c1.w - c0.w); c0 = mix(c0, c1, f); P0 = mix(P0, P1, f); }
    if (c1.w < 0.08) { float f = (0.08 - c1.w) / (c0.w - c1.w); c1 = mix(c1, c0, f); P1 = mix(P1, P0, f); }
    float halfPx = 1.5, quadHalf = halfPx + 6.0;
    vec4 cl = aEnd < 0.5 ? widen(c0, c1, aSide * quadHalf) : widen(c1, c0, -aSide * quadHalf);
    gl_Position = cl;
    vAcross = aSide * quadHalf; vHalf = halfPx;
    vec3 P = aEnd < 0.5 ? P0 : P1;
    vA = a * exp(-length(P - uCamPos) / uFade) * 0.85;
    vCol = col;
    vNdc = cl.xy / cl.w;
  }
`;

// THE WALL: the main tube's shell, 5 % dark and translucent, a faint sheen at
// grazing angles so the volume reads. Base: aK (0..16), aEnd. Instance: aRing.
const WALL_VERTEX = `
  precision highp float;
  attribute float aK;
  attribute float aEnd;
  attribute float aRing;
  ${PATH_CHUNK}
  varying float vGraze;
  varying float vA;
  varying vec2 vNdc;
  void main() {
    float k = uMainBase + aRing + aEnd;
    float ahead = k * ${S.toFixed(1)} - uTravel;
    if ((ahead < -1.0 && uBack < 0.5) || ahead > uMainCount * ${S.toFixed(1)}) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    vec3 C, N, B; mainSample(k, C, N, B);
    float ang = 6.2831853 * aK / 16.0;
    vec3 nrm = cos(ang) * N + sin(ang) * B;
    vec3 P = C + uRadius * 1.02 * nrm;
    vec4 cl = project(P);
    gl_Position = cl;
    vec3 v = normalize(uCamPos - P);
    vGraze = 1.0 - abs(dot(nrm, v));
    vA = exp(-length(P - uCamPos) / uFade) * smoothstep(uMainCount, uMainCount - 8.0, ahead);
    vNdc = cl.xy / max(cl.w, 1e-4);
  }
`;
const WALL_FRAGMENT = `
  precision highp float;
  uniform vec3 uWallTint;
  uniform float uGain;
  varying float vGraze;
  varying float vA;
  varying vec2 vNdc;
  void main() {
    float g = vGraze * vGraze * vGraze;
    gl_FragColor = vec4(uWallTint * (0.03 + 0.10 * g) * vA * uGain, 0.05 * vA);
  }
`;

// ----------------------------------------------------------------- geometry

function ringBase(K) {
  const pos = [], aK = [], aSide = [], idx = [];
  for (let k = 0; k <= K; k++) for (const s of [-1, 1]) { pos.push(0, 0, 0); aK.push(k); aSide.push(s); }
  for (let k = 0; k < K; k++) { const a = k * 2; idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('aK', new THREE.Float32BufferAttribute(aK, 1));
  g.setAttribute('aSide', new THREE.Float32BufferAttribute(aSide, 1));
  g.setIndex(idx);
  return g;
}
function longBase() {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(new Array(12).fill(0), 3));
  g.setAttribute('aEnd', new THREE.Float32BufferAttribute([0, 0, 1, 1], 1));
  g.setAttribute('aSide', new THREE.Float32BufferAttribute([-1, 1, -1, 1], 1));
  g.setIndex([0, 1, 2, 1, 3, 2]);
  return g;
}
function wallBase() {
  const pos = [], aK = [], aEnd = [], idx = [];
  for (let k = 0; k <= 16; k++) for (const e of [0, 1]) { pos.push(0, 0, 0); aK.push(k); aEnd.push(e); }
  for (let k = 0; k < 16; k++) { const a = k * 2; idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('aK', new THREE.Float32BufferAttribute(aK, 1));
  g.setAttribute('aEnd', new THREE.Float32BufferAttribute(aEnd, 1));
  g.setIndex(idx);
  return g;
}
function instanced(base, count, attrs) {
  const g = new THREE.InstancedBufferGeometry();
  g.index = base.index;
  for (const n of Object.keys(base.attributes)) g.setAttribute(n, base.getAttribute(n));
  for (const [name, arr] of Object.entries(attrs)) g.setAttribute(name, new THREE.InstancedBufferAttribute(arr, 1));
  g.instanceCount = count;
  g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e7);
  return g;
}

// =============================================================================

export class HyperloopVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIER[o.qualityTier] ? o.qualityTier : 2;
    this._layout = TIER[this._tier];
    this._forkDebug = urlParam('loopfork') === '1';
    this._outsideDebug = urlParam('loopoutside') === '1';
    const sp = parseFloat(urlParam('loopspeed'));
    this._speedDebug = Number.isFinite(sp) && sp > 0 ? sp : 1;
    this._post = new VdjPost({ bloomStrength: 0.35, bloomThreshold: 0.78, grain: 0.015, vignette: 0.35 });
    this._comfort = new VdjComfort(VDJ_PRESETS.party.comfort);
    if (urlParam('loopprobe') === '1' && typeof window !== 'undefined') window.__hyperloop = this;
  }

  get postProcessing() { return this._post.value; }

  init(scene, camera) {
    this._scene = scene; this._camera = camera;
    this._prevBackground = scene.background;
    scene.background = new THREE.Color(0x000000);
    this._group = new THREE.Group(); scene.add(this._group);
    const L = this._layout;
    this._sideSlots = L.normal + L.net;
    this._rows = 3 + 3 * this._sideSlots;
    this._pathData = new Float32Array(256 * this._rows * 4);
    this._pathTex = new THREE.DataTexture(this._pathData, 256, this._rows, THREE.RGBAFormat, THREE.FloatType);
    this._pathTex.minFilter = this._pathTex.magFilter = THREE.NearestFilter; this._pathTex.needsUpdate = true;
    this._colData = new Uint8Array(CAP * 4);
    this._colTex = new THREE.DataTexture(this._colData, CAP, 1, THREE.RGBAFormat);
    this._colTex.minFilter = this._colTex.magFilter = THREE.NearestFilter; this._colTex.needsUpdate = true;
    const safe = { uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather };
    this._gain = { value: 1 };
    const U = this._U = Object.assign({
      uPath: { value: this._pathTex }, uRows: { value: this._rows }, uRingCol: { value: this._colTex },
      uTravel: { value: 0 }, uMainBase: { value: 0 }, uMainCount: { value: L.rings }, uRadius: { value: R },
      uCamPos: { value: new THREE.Vector3() }, uFade: { value: FADE_IN }, uNet: { value: 0 }, uBack: { value: 0 },
      uResolution: { value: new THREE.Vector2(1280, 720) }, uTwist: { value: 0 },
      uFlashRing: { value: -1 }, uFlashAmt: { value: 0 }, uWhite: { value: 0 }, uBandsLow: { value: 0 },
      uLongs: { value: L.longs }, uLongCol: { value: new THREE.Color(1, 1, 1) }, uWallTint: { value: new THREE.Color(0.3, 0.6, 1) },
      uGain: this._gain
    }, safe);
    const add = { transparent: true, depthWrite: false, depthTest: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide };
    const ringMat = new THREE.ShaderMaterial(Object.assign({ uniforms: U, vertexShader: ringVertex(L.ringK), fragmentShader: LINE_FRAGMENT }, add));
    const longMat = new THREE.ShaderMaterial(Object.assign({ uniforms: U, vertexShader: LONG_VERTEX, fragmentShader: LINE_FRAGMENT }, add));
    const wallMat = new THREE.ShaderMaterial({ uniforms: U, vertexShader: WALL_VERTEX, fragmentShader: WALL_FRAGMENT, transparent: true, depthWrite: false, depthTest: false, side: THREE.DoubleSide, blending: THREE.NormalBlending });
    this._mats = [ringMat, longMat, wallMat];
    this._bases = [ringBase(L.ringK), longBase(), wallBase()];
    const mainN = BACK + L.rings + 1;
    // main tube instances
    const seq = (n, f) => { const a = new Float32Array(n); for (let i = 0; i < n; i++) a[i] = f(i); return a; };
    const mainRings = instanced(this._bases[0], mainN, { aTube: seq(mainN, () => -1), aRing: seq(mainN, (i) => i) });
    const mainLongs = instanced(this._bases[1], mainN * L.longs, { aTube: seq(mainN * L.longs, () => -1), aRing: seq(mainN * L.longs, (i) => Math.floor(i / L.longs)), aLon: seq(mainN * L.longs, (i) => i % L.longs) });
    const wall = instanced(this._bases[2], mainN, { aRing: seq(mainN, (i) => i) });
    // side tubes
    const sN = this._sideSlots * SIDE_RINGS, sL = Math.min(4, L.longs);
    const sideRings = instanced(this._bases[0], sN, { aTube: seq(sN, (i) => Math.floor(i / SIDE_RINGS)), aRing: seq(sN, (i) => i % SIDE_RINGS) });
    const sideLongs = instanced(this._bases[1], sN * sL, { aTube: seq(sN * sL, (i) => Math.floor(i / (SIDE_RINGS * sL))), aRing: seq(sN * sL, (i) => Math.floor(i / sL) % SIDE_RINGS), aLon: seq(sN * sL, (i) => i % sL) });
    this._geos = [mainRings, mainLongs, wall, sideRings, sideLongs];
    // draw order: the other tubes, then the wall (darkens them 5 %), then our tube
    const mk = (g, m, order) => { const x = new THREE.Mesh(g, m); x.frustumCulled = false; x.renderOrder = order; this._group.add(x); return x; };
    this._sideRingMesh = mk(sideRings, ringMat, 1);
    this._sideLongMesh = mk(sideLongs, longMat, 1);
    this._wallMesh = mk(wall, wallMat, 2);
    this._mainLongMesh = mk(mainLongs, longMat, 3);
    this._mainRingMesh = mk(mainRings, ringMat, 4);
    const self = this;
    this._mainRingMesh.onBeforeRender = function (renderer) { renderer.getDrawingBufferSize(_size); self._U.uResolution.value.set(_size.x, _size.y); };

    // state
    this._time = 0; this._beatsAbs = null; this._lastBp = null; this._lastBarP = null; this._lastPhraseP = null;
    this._beatCount = 0; this._barCount = 0; this._phraseCount = 0;
    this._B0 = null; this._T0 = 0; this._rate = 1; this._travel = 0;
    this._secondsBeat = 0.5; this._bands = { low: 0.5, mid: 0.5, high: 0.5 }; this._energy = 0.5;
    this._paletteKey = null; this._palette = FALLBACK.map((c) => vdjColor(c)); this._palIdx = 0;
    this._colA = new THREE.Color(); this._colB = new THREE.Color();
    this._helix = false; this._flash = 0; this._whiteFrames = 0;
    this._drop = null; this._lastDropAt = -1e9;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null; this._lastMode = null;
    this._bank = 0;
    this._stats = { forks: 0, crossings: 0, segments: 0, uploads: 0, maxRollDeg: 0, maxFrameFlipDeg: 0 };
    this._applyPalette(null);
    this._resetPath(new THREE.Vector3(0, 0, 0), 0);
    this._outsidePending = this._outsideDebug;
  }

  // ------------------------------------------------------------ the path

  _resetPath(p, yaw) {
    this._main = { st: new TubeState(p, yaw, 0), pos: new Float32Array(CAP * 3), tan: new Float32Array(CAP * 3), genK: 0, seg: 0 };
    this._sideNext = 0;
    this._sides = new Array(this._sideSlots).fill(null);
    this._bornK = 0;
    // sample 0
    const st = this._main.st;
    this._writeMain(0, { p: st.p.clone(), t: st.t.clone(), n: st.n.clone(), b: new THREE.Vector3().crossVectors(st.t, st.n).normalize() });
    this._main.genK = 1;
    this._pathDirty = true;
  }

  _writeMain(k, s) {
    const c = k % CAP, D = this._pathData;
    const o0 = (0 * 256 + c) * 4, o1 = (1 * 256 + c) * 4, o2 = (2 * 256 + c) * 4;
    D[o0] = s.p.x; D[o0 + 1] = s.p.y; D[o0 + 2] = s.p.z; D[o0 + 3] = 1;
    D[o1] = s.n.x; D[o1 + 1] = s.n.y; D[o1 + 2] = s.n.z;
    D[o2] = s.b.x; D[o2 + 1] = s.b.y; D[o2 + 2] = s.b.z;
    const m = this._main;
    m.pos[c * 3] = s.p.x; m.pos[c * 3 + 1] = s.p.y; m.pos[c * 3 + 2] = s.p.z;
    m.tan[c * 3] = s.t.x; m.tan[c * 3 + 1] = s.t.y; m.tan[c * 3 + 2] = s.t.z;
    // criterion 3: the frame never jumps between two samples
    if (this._prevN) {
      const ang = THREE.MathUtils.radToDeg(Math.acos(THREE.MathUtils.clamp(this._prevN.dot(s.n), -1, 1)));
      this._stats.maxFrameFlipDeg = Math.max(this._stats.maxFrameFlipDeg, ang);
    }
    this._prevN = s.n.clone();
  }

  _writeSide(j, samples, kind, travelAtBirth, flowSign, colour) {
    const D = this._pathData, r = 3 + 3 * j;
    const n = Math.min(samples.length, SIDE_LEN);
    for (let c = 0; c < 256; c++) for (let q = 0; q < 3; q++) { const o = ((r + q) * 256 + c) * 4; D[o] = D[o + 1] = D[o + 2] = D[o + 3] = 0; }
    for (let i = 0; i < n; i++) {
      const s = samples[i];
      const o0 = (r * 256 + i) * 4, o1 = ((r + 1) * 256 + i) * 4, o2 = ((r + 2) * 256 + i) * 4;
      D[o0] = s.p.x; D[o0 + 1] = s.p.y; D[o0 + 2] = s.p.z; D[o0 + 3] = 1;
      D[o1] = s.n.x; D[o1 + 1] = s.n.y; D[o1 + 2] = s.n.z;
      D[o2] = s.b.x; D[o2 + 1] = s.b.y; D[o2 + 2] = s.b.z;
    }
    const p = (r * 256 + 255) * 4, q = ((r + 1) * 256 + 255) * 4;
    D[p] = n; D[p + 1] = travelAtBirth; D[p + 2] = kind; D[p + 3] = flowSign;
    D[q] = colour.r; D[q + 1] = colour.g; D[q + 2] = colour.b;
    this._sides[j] = { kind, n, samples: samples.slice(0, n), birth: travelAtBirth };
    this._pathDirty = true;
  }
  _clearSide(j) {
    const D = this._pathData, p = ((3 + 3 * j) * 256 + 255) * 4;
    D[p] = 0; this._sides[j] = null; this._pathDirty = true;
  }
  _allocNormal() { const j = this._sideNext; this._sideNext = (this._sideNext + 1) % this._layout.normal; return j; }

  // ONE SEGMENT = one bar of flight. A fork bar (one in two) doubles it: the
  // followed tube and a branch leave the same state, separate sideways over
  // the first half-bar, then their headings diverge.
  _growMain() {
    const m = this._main, st = m.st, seg = m.seg++;
    const r = rng(9157 + seg * 7919);
    const fork = this._forkDebug ? seg > 0 : (seg % 2 === 1);
    const side = r() < 0.5 ? -1 : 1;
    let yawTo, pitchTo = THREE.MathUtils.clamp(st.pitch + (r() - 0.5) * 0.5, -MAX_PITCH, MAX_PITCH);
    // keep the long-run heading wandering, not spiralling: pull back toward the bar's mean
    // a fork: the two tubes run almost side by side through the bar, so the
    // branch stays in view; it drifts away over the following bars
    if (fork) yawTo = st.yaw + side * FORK_TURN * 0.35;
    else yawTo = st.yaw + (r() < 0.5 ? -1 : 1) * (MIN_TURN + r() * (MAX_TURN - MIN_TURN));
    // mostly sideways: the vertical field of view is the narrower one
    const qKind = r() < 0.85 ? 'h' : 'v';
    const start = st.clone();
    const out = [];
    const off = fork ? (u) => side * FORK_OFF_MAIN * sm(u * 2) : null;
    growSegment(st, BAR, yawTo, pitchTo, out, off, qKind);
    const kStart = m.genK;
    for (const s of out) { this._writeMain(m.genK, s); m.genK++; }
    this._stats.segments++;
    if (fork) {
      // the branch: the mirror offset, the other heading, then four more bars drifting away
      const b = start.clone(), bs = [];
      bs.push({ p: start.p.clone(), t: start.t.clone(), n: start.n.clone(), b: new THREE.Vector3().crossVectors(start.t, start.n).normalize() });
      growSegment(b, BAR, start.yaw - side * FORK_TURN * 0.35, pitchTo, bs, (u) => -side * FORK_OFF_BRANCH * sm(u * 2), qKind);
      for (let k = 0; k < 6; k++) growSegment(b, BAR, b.yaw - side * FORK_TURN * 0.45, THREE.MathUtils.clamp(b.pitch + (r() - 0.5) * 0.3, -MAX_PITCH, MAX_PITCH), bs, null, 'h');
      const j = this._allocNormal();
      // its rings flow away from the fork at our speed; born where the fork starts on the travel axis
      this._writeSide(j, bs, KIND.branch, kStart * S, 1, this._colB);
      this._stats.forks++;
    } else if (this._layout.crossings && r() < 0.45) {
      // a crossing: another stream passing above or below the middle of this bar, no junction
      const mid = out[BAR / 2], up = r() < 0.5 ? 1 : -1;
      const h = up * (2 * R + 1.0 + r() * 1.2);
      const across = new THREE.Vector3().crossVectors(mid.t, UP).normalize();
      const yaw = Math.atan2(across.x, -across.z) + (r() - 0.5) * 0.4;
      const p0 = mid.p.clone().addScaledVector(UP, h).addScaledVector(dirOf(yaw, 0, new THREE.Vector3()), -SIDE_LEN * S / 2);
      const cs = new TubeState(p0, yaw, (r() - 0.5) * 0.15);
      const cx = [{ p: cs.p.clone(), t: cs.t.clone(), n: cs.n.clone(), b: new THREE.Vector3().crossVectors(cs.t, cs.n).normalize() }];
      for (let k = 0; k < SIDE_LEN / BAR; k++) growSegment(cs, BAR, cs.yaw + (r() - 0.5) * 0.25, cs.pitch * 0.5, cx, null, 'h');
      // keep its middle exactly over the crossing point: it was built straight-ish around it
      const j = this._allocNormal();
      this._writeSide(j, cx, KIND.crossing, this._travel, r() < 0.5 ? 1 : -1, this._colA);
      this._stats.crossings++;
    }
  }

  // THE NETWORK seen on a drop: dozens of tubes interlacing around a centre ahead
  _buildNetwork(centre, seed) {
    const r = rng(seed);
    const L = this._layout;
    for (let i = 0; i < L.net; i++) {
      const j = L.normal + i;
      // start on a sphere, aim through the core, wander
      const th = r() * Math.PI * 2, ph = (r() - 0.5) * 1.2, rad = 26 + r() * 8;
      const p0 = new THREE.Vector3(Math.cos(th) * Math.cos(ph), Math.sin(ph) * 0.6, Math.sin(th) * Math.cos(ph)).multiplyScalar(rad).add(centre);
      const aim = new THREE.Vector3((r() - 0.5) * 14, (r() - 0.5) * 8, (r() - 0.5) * 14).add(centre).sub(p0).normalize();
      const yaw = Math.atan2(aim.x, -aim.z), pitch = Math.asin(THREE.MathUtils.clamp(aim.y, -0.6, 0.6));
      const st = new TubeState(p0, yaw, pitch);
      const s = [{ p: st.p.clone(), t: st.t.clone(), n: st.n.clone(), b: new THREE.Vector3().crossVectors(st.t, st.n).normalize() }];
      for (let k = 0; k < SIDE_LEN / BAR; k++) growSegment(st, BAR, st.yaw + (r() - 0.5) * 1.2, THREE.MathUtils.clamp(st.pitch + (r() - 0.5) * 0.6, -0.7, 0.7), s, null, 'h');
      const col = this._palette[(i + this._palIdx) % this._palette.length].clone();
      if (i % 3 === 2) col.offsetHSL(0.5, 0, 0);
      this._writeSide(j, s, KIND.net, this._travel - r() * 40, r() < 0.5 ? 1 : -1, col);
    }
  }

  // main path position (Catmull-Rom between samples) and tangent at arc length s
  _pathAt(s, outP, outT) {
    const m = this._main;
    const u = s / S, i = Math.floor(u), f = u - i;
    const lo = Math.max(0, m.genK - CAP + 2);
    const g = (slot, k) => { k = THREE.MathUtils.clamp(k, lo, m.genK - 1); const c = (k % CAP) * 3; return _cr[slot].set(m.pos[c], m.pos[c + 1], m.pos[c + 2]); };
    const p0 = g(0, i - 1), p1 = g(1, i), p2 = g(2, i + 1), p3 = g(3, i + 2);
    const f2 = f * f, f3 = f2 * f;
    outP.set(0, 0, 0)
      .addScaledVector(p0, -0.5 * f3 + f2 - 0.5 * f)
      .addScaledVector(p1, 1.5 * f3 - 2.5 * f2 + 1)
      .addScaledVector(p2, -1.5 * f3 + 2 * f2 + 0.5 * f)
      .addScaledVector(p3, 0.5 * f3 - 0.5 * f2);
    if (outT) {
      outT.set(0, 0, 0)
        .addScaledVector(p0, -1.5 * f2 + 2 * f - 0.5)
        .addScaledVector(p1, 4.5 * f2 - 5 * f)
        .addScaledVector(p2, -4.5 * f2 + 4 * f + 0.5)
        .addScaledVector(p3, 1.5 * f2 - f).normalize();
    }
    return outP;
  }

  // ------------------------------------------------------------ colours

  _applyPalette(pal) {
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : FALLBACK;
    const key = v.map(String).join('|');
    if (key === this._paletteKey) return;
    this._paletteKey = key;
    this._palette = v.map((c) => vdjColor(c));
    if (this._palette.length < 3) {
      // R4: complete with hues a third of the turn away
      const base = this._palette[0];
      for (const dh of [0.33, 0.66]) if (this._palette.length < 3) this._palette.push(base.clone().offsetHSL(dh, 0, 0));
    }
    this._setColours();
  }
  _setColours() {
    const P = this._palette, n = P.length;
    this._colA.copy(P[this._palIdx % n]);
    this._colB.copy(P[(this._palIdx + 1) % n]);
    // R4: two colours that must read apart are a quarter of the turn apart
    const ha = {}, hb = {}; this._colA.getHSL(ha); this._colB.getHSL(hb);
    let dh = Math.abs(ha.h - hb.h); dh = Math.min(dh, 1 - dh);
    if (dh < 0.25) this._colB.copy(this._colA).offsetHSL(0.4, 0, 0);
    this._U.uLongCol.value.copy(this._colB);
    this._U.uWallTint.value.copy(this._colA);
  }
  // a ring is born at the far end with the colour and width of the moment
  _bornColour(k) {
    // between the two colours along the HUE (an RGB mix of two saturated
    // colours is a pale one)
    const c = _c.copy(this._colA).lerpHSL(this._colB, clamp01(this._bands.high * 1.3 - 0.15));
    const o = (k % CAP) * 4;
    this._colData[o] = Math.round(clamp01(c.r) * 255); this._colData[o + 1] = Math.round(clamp01(c.g) * 255); this._colData[o + 2] = Math.round(clamp01(c.b) * 255);
    this._colData[o + 3] = Math.round(clamp01(this._energy) * 255);
    this._colDirty = true;
  }

  // ------------------------------------------------------------ the drop

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) this._dropIdx = 0;
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  _startDrop(forever) {
    // the network is centred a bar ahead on our own tube; the camera leaves the
    // tube upward and backward, looks at it for 2 bars, then dives back in
    const centre = new THREE.Vector3();
    this._pathAt(this._travel + BAR * S, centre, _t1);
    const back = new THREE.Vector3(_t1.x, 0, _t1.z); if (back.lengthSq() < 1e-4) back.set(0, 0, 1); back.normalize().negate();
    this._buildNetwork(centre, 4049 + this._stats.forks * 31 + Math.round(this._travel));
    this._drop = { b0: this._beatsAbs != null ? this._beatsAbs : 0, centre, back, forever: !!forever };
    this._lastDropAt = this._time;
  }

  // ------------------------------------------------------------ the camera

  _outsideWeight() {
    if (!this._drop) return 0;
    if (this._drop.forever) return 1;
    const b = (this._beatsAbs != null ? this._beatsAbs : 0) - this._drop.b0;
    if (b < 0) return 0;
    if (b < 1) return 1 - Math.pow(1 - b, 3);          // it bursts out
    if (b < 7) return 1;
    if (b < 8) return 1 - sm(b - 7);                   // and dives back
    return 0;
  }

  _placeCamera(dt) {
    const cam = this._camera;
    const s = this._travel;
    // inside: on the axis, aiming LOOK ahead, banked with the curvature
    this._pathAt(s, _p1, _t1);
    this._pathAt(s + LOOK * Math.pow(this._rate * this._speedDebug, 0.6), _p2, null);
    this._pathAt(s + 3, _p3, _t3);
    // curvature in the horizontal plane -> roll, bounded
    const yaw1 = Math.atan2(_t1.x, -_t1.z), yaw3 = Math.atan2(_t3.x, -_t3.z);
    let dy = yaw3 - yaw1; while (dy > Math.PI) dy -= 2 * Math.PI; while (dy < -Math.PI) dy += 2 * Math.PI;
    // into the turn (a right turn tilts the camera's up to the right), bounded
    const bankTo = THREE.MathUtils.clamp(dy / 3 * BANK_K, -MAX_ROLL, MAX_ROLL);
    this._bank += (bankTo - this._bank) * (dt > 0 ? 1 - Math.exp(-dt / 0.25) : 0);
    // the aim, low-passed: at x4 the same bends go by four times faster
    const fwdTo = _v1.subVectors(_p2, _p1).normalize();
    if (!this._aim || dt <= 0 && !this._aimInit) { this._aim = fwdTo.clone(); this._aimInit = true; }
    else if (dt > 0) this._aim.lerp(fwdTo, 1 - Math.exp(-dt / AIM_TAU)).normalize();
    const fwd = _v5.copy(this._aim);
    _p2.copy(_p1).addScaledVector(fwd, 6);
    const right = _v2.crossVectors(fwd, UP).normalize();
    const upIn = _v3.crossVectors(right, fwd).normalize();
    upIn.applyAxisAngle(fwd, this._bank);
    const w = this._outsideWeight();
    let pos = _p1, look = _p2, up = upIn, fov = FOV_IN;
    if (w > 0) {
      const d = this._drop;
      // outside: high and behind the network's centre, a slow orbit
      const b = (this._beatsAbs != null ? this._beatsAbs : 0) - d.b0;
      const orbit = d.forever ? this._time * 0.08 : b / 8 * 0.6;
      const off = _v4.copy(d.back).multiplyScalar(38).applyAxisAngle(UP, orbit).addScaledVector(UP, 18);
      const outPos = _p4.copy(d.centre).add(off);
      pos = _pm.copy(_p1).lerp(outPos, w);
      look = _pl.copy(_p2).lerp(d.centre, w);
      up = _pu.copy(upIn).lerp(UP, w).normalize();
      fov = FOV_IN + (FOV_OUT - FOV_IN) * w;
    }
    cam.fov = fov; cam.near = 0.05; cam.far = 1000;
    cam.position.copy(pos);
    cam.up.copy(up);
    cam.lookAt(look);
    cam.updateProjectionMatrix(); cam.updateMatrixWorld();
    this._U.uCamPos.value.copy(pos);
    this._U.uFade.value = FADE_IN + (FADE_OUT - FADE_IN) * w;
    this._U.uNet.value = w;
    this._U.uBack.value = w > 0.01 ? 1 : 0;
    // comfort probe: the roll actually applied (angle between up and the vertical plane through fwd)
    if (w < 0.01) this._stats.maxRollDeg = Math.max(this._stats.maxRollDeg, Math.abs(THREE.MathUtils.radToDeg(this._bank)));
  }

  // ------------------------------------------------------------ update

  update(deltaTime, profile) {
    this._applyPalette(profile.artworkPalette);
    if (profile.playbackState !== 'PLAYING') { this._placeCamera(0); this._post.update(1); return; }   // R11: a freeze
    const dt = Math.min(deltaTime, 0.1);
    const t = this._time += dt;
    const director = profile.director;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    this._secondsBeat = bpm > 0 ? 60 / THREE.MathUtils.clamp(bpm, 60, 200) : 0.5;            // without a BPM: 120
    const beatS = this._secondsBeat;
    const bands = director && director.bands ? director.bands : null;
    const kb = 1 - Math.exp(-dt / 0.12);
    for (const n of ['low', 'mid', 'high']) this._bands[n] += ((bands && bands[n] != null ? clamp01(bands[n]) : 0.5) - this._bands[n]) * kb;
    const energyTo = director && director.intensity != null ? clamp01(director.intensity) : (this._bands.low + this._bands.mid + this._bands.high) / 3;
    this._energy += (energyTo - this._energy) * kb;

    // THE BEAT CLOCK: absolute beats from the phase (integers on the beats)
    let bp = profile.beatPhase;
    if (bp == null) { bp = ((this._freeBp || 0) + dt / beatS) % 1; this._freeBp = bp; }
    let beat = false, bar = false, phrase = false;
    if (this._lastBp == null) this._beatsAbs = bp;
    else {
      let d = bp - this._lastBp;
      if (d < -0.5) { d += 1; beat = true; }
      if (d < 0) d = 0;
      this._beatsAbs += d;
    }
    this._lastBp = bp;
    const barP = profile.barPhase;
    if (barP != null) { if (this._lastBarP != null && barP < this._lastBarP - 0.5) bar = true; this._lastBarP = barP; }
    else if (beat && this._beatCount % 4 === 3) bar = true;
    const phP = profile.phrasePhase;
    if (phP != null) { if (this._lastPhraseP != null && phP < this._lastPhraseP - 0.5) phrase = true; this._lastPhraseP = phP; }
    else if (beat && this._beatCount % 16 === 15) phrase = true;
    if (beat) this._beatCount++;
    if (bar) this._barCount++;
    // anchor the travel on a bar start: segments begin on downbeats
    if (this._B0 == null) { this._B0 = this._beatsAbs - (barP != null ? barP * 4 : 0); this._T0 = 0; }

    // MONTÉE: x2, then x4 in the last bar; the speed only changes on a downbeat
    const next = director && director.nextEvent;
    const dropMs = next && next.type === 'drop' && next.msUntil != null ? next.msUntil : null;
    if (bar) {
      let rate = 1;
      if ((dropMs != null && dropMs < 16 * beatS * 1000) || (director && director.mode === 'build')) rate = 2;
      if (dropMs != null && dropMs < 4 * beatS * 1000 + 50) rate = 4;
      if (this._drop) rate = 1;
      this._setRate(rate);
    }
    this._travel = this._T0 + 2 * S * this._rate * this._speedDebug * (this._beatsAbs - this._B0);

    // DROP
    let drop = this._tickDrops(profile);
    if (director && director.mode === 'drop' && this._lastMode !== 'drop' && director.hasReliableGrid) drop = true;
    this._lastMode = director ? director.mode : null;
    if (drop && !this._drop && t - this._lastDropAt > 8 * beatS) {
      this._setRate(1);
      this._travel = this._T0 + 2 * S * this._rate * this._speedDebug * (this._beatsAbs - this._B0);
      this._startDrop(false);
      if (this._comfort.requestFlash(t)) { this._flash = 1; this._whiteFrames = 2; if (director) director.flash = Math.max(director.flash || 0, VDJ_DROP_FLASH); }
    }
    if (this._drop && !this._drop.forever && this._beatsAbs - this._drop.b0 >= 8) {
      for (let i = 0; i < this._layout.net; i++) this._clearSide(this._layout.normal + i);
      this._drop = null;
    }

    // PHRASE: the next colours, straight or helical longerons
    if (phrase) { this._palIdx++; this._setColours(); this._helix = !this._helix; }

    // grow the path ahead; release the side tubes far behind
    const need = this._travel + (this._layout.rings + 2 * BAR) * S;
    while (this._main.genK * S < need) this._growMain();
    for (let j = 0; j < this._layout.normal; j++) {
      const sd = this._sides[j];
      if (sd && sd.kind === KIND.branch && this._travel - sd.birth > (SIDE_LEN + 40) * S) this._clearSide(j);
      if (sd && sd.kind === KIND.crossing && this._travel - sd.birth > 160 * S) this._clearSide(j);
    }
    if (this._outsidePending) { this._outsidePending = false; this._startDrop(true); }
    // rings born at the far end
    const kCam = Math.floor(this._travel / S);
    while (this._bornK <= kCam + this._layout.rings) { this._bornColour(this._bornK); this._bornK++; }

    // uniforms
    const U = this._U;
    U.uTravel.value = this._travel;
    U.uMainBase.value = kCam - BACK;
    U.uMainCount.value = this._layout.rings;
    U.uBandsLow.value = this._bands.low;
    // the beat's flash: at the beat the ring 2 x rate spacings ahead is the one
    // crossing the frame's edge (a ring of radius R leaves a 74 degree frame at
    // ~1.9 units); it flashes and fades over 0.15 s
    if (beat) { this._flashRing = Math.round(this._travel / S) + 2 * this._rate; this._flashT = t; }
    U.uFlashRing.value = this._flashRing != null ? this._flashRing : -1;
    U.uFlashAmt.value = this._flashT != null ? Math.exp(-(t - this._flashT) / 0.15) : 0;
    U.uTwist.value = this._helix ? 0.35 : 0;
    U.uWhite.value = this._whiteFrames > 0 ? 1 : 0;
    if (this._whiteFrames > 0) this._whiteFrames--;
    this._flash *= Math.exp(-dt / 0.15);
    const est = 0.05 + 0.04 * this._energy + 0.6 * this._flash + 0.05 * this._outsideWeight();
    // outside, dozens of additive tubes overlap: the whole network is dimmed
    const outsideDim = 1 - 0.65 * this._outsideWeight();
    this._gain.value = this._comfort.measure(est, dt) * (1 + 0.5 * this._flash) * outsideDim;
    if (this._pathDirty) { this._pathTex.needsUpdate = true; this._pathDirty = false; this._stats.uploads++; }
    if (this._colDirty) { this._colTex.needsUpdate = true; this._colDirty = false; }
    this._placeCamera(dt);
    this._post.update(vdjBeatChroma(bp, beatS));
  }

  _setRate(rate) {
    if (rate === this._rate) return;
    // keep the lock: re-anchor on the current beat, travel on a whole spacing
    this._T0 = Math.round(this._travel / S) * S;
    this._B0 = Math.round(this._beatsAbs);
    this._rate = rate;
  }

  getProbe() {
    return {
      tier: this._tier, beats: this._beatsAbs, travel: this._travel, rate: this._rate, bars: this._barCount,
      stats: Object.assign({}, this._stats), drop: !!this._drop, outside: +this._outsideWeight().toFixed(3),
      bankDeg: +THREE.MathUtils.radToDeg(this._bank).toFixed(2), helix: this._helix,
      sides: this._sides.map((s) => (s ? s.kind : 0))
    };
  }

  dispose() {
    if (this._scene) { this._scene.remove(this._group); this._scene.background = this._prevBackground || null; }
    for (const g of [...(this._geos || []), ...(this._bases || [])]) g.dispose();
    for (const m of this._mats || []) m.dispose();
    if (this._pathTex) this._pathTex.dispose();
    if (this._colTex) this._colTex.dispose();
    if (typeof window !== 'undefined' && window.__hyperloop === this) window.__hyperloop = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
const _c = new THREE.Color();
const _cr = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()];
const _p1 = new THREE.Vector3(), _p2 = new THREE.Vector3(), _p3 = new THREE.Vector3(), _p4 = new THREE.Vector3();
const _t1 = new THREE.Vector3(), _t3 = new THREE.Vector3();
const _pm = new THREE.Vector3(), _pl = new THREE.Vector3(), _pu = new THREE.Vector3();
const _v1 = new THREE.Vector3(), _v2 = new THREE.Vector3(), _v3 = new THREE.Vector3(), _v4 = new THREE.Vector3(), _v5 = new THREE.Vector3();
